/** Channels a reader has turned off (Tamar, 2026-10-28: "on frontend we
 *  don't want to see every channel the sensor is transmitting, so being able
 *  to turn some channels off at frontend level would be great").
 *
 *  Display only, and only for this reader: nothing is removed, nothing stops
 *  being collected or stored, exports and analyses still read every channel,
 *  and Show all brings them back. Kept as one of the person's workspace
 *  documents (`hidden-channels`, db/045, `lib/userDocuments`), so the choice
 *  follows them from one browser to the next; Tamar's preview kept it in
 *  `localStorage`, and a copy found there is carried over once
 *  (`carryOverHidden`). Ids are "station|parameter".
 *
 *  The server's copy refuses writes until it has loaded, so a write can
 *  fail: the setters return false and the caller says nothing was saved. */
import { useEffect, useState } from 'react'
import { documentsStatus, serverStorage, subscribeDocuments } from './userDocuments'

/** The browser key the preview used, and the document's name for it
 *  (`userDocuments.DOCUMENT_KEYS`). */
export const HIDDEN_KEY = 'hydro.hidden-channels.v1'
const EVENT = 'hydro-hidden-channels'

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export function parseHidden(raw: string | null): Set<string> {
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [])
  } catch { return new Set() }
}

export function readHidden(storage?: Store): Set<string> {
  try { return parseHidden((storage ?? serverStorage).getItem(HIDDEN_KEY)) } catch { return new Set() }
}

/** Saved at once in the page and sent a moment later; false when the
 *  person's documents have not loaded, and nothing was kept. */
function write(next: Set<string>, storage?: Store): boolean {
  try {
    const target = storage ?? serverStorage
    if (next.size) target.setItem(HIDDEN_KEY, JSON.stringify([...next].sort()))
    else target.removeItem(HIDDEN_KEY)
  } catch { return false }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENT))
  return true
}

export function setChannelHidden(id: string, hidden: boolean, storage?: Store): boolean {
  const next = readHidden(storage)
  if (hidden) next.add(id); else next.delete(id)
  return write(next, storage)
}

/** Show every hidden channel of one station again. */
export function showAllChannels(station: string, storage?: Store): boolean {
  return write(new Set([...readHidden(storage)].filter((id) => !id.startsWith(`${station}|`))), storage)
}

/** A copy kept in this browser by the preview is added to the person's
 *  document, once, and then removed from the browser. Nothing is carried
 *  while the documents are not loaded (the copy stays for next time), and a
 *  hidden channel the document already holds is never un-hidden by it.
 *  Returns how many were added, or null when there was nothing to do. */
export function carryOverHidden(browser?: Store | null, server?: Store): number | null {
  let local: Store | null | undefined = browser
  if (local === undefined) {
    try { local = typeof window === 'undefined' ? null : window.localStorage } catch { local = null }
  }
  if (!local) return null
  let raw: string | null
  try { raw = local.getItem(HIDDEN_KEY) } catch { return null }
  if (raw === null) return null
  if (!server && documentsStatus() !== 'ready') return null
  const kept = readHidden(server)
  const merged = new Set([...kept, ...parseHidden(raw)])
  if (merged.size !== kept.size && !write(merged, server)) return null
  try { local.removeItem(HIDDEN_KEY) } catch { /* It is only read again, and only once. */ }
  return merged.size - kept.size
}

/** The hidden channels, kept current across every page that reads them. */
export function useHiddenChannels(): Set<string> {
  const [hidden, setHidden] = useState(() => readHidden())
  useEffect(() => {
    const refresh = () => { carryOverHidden(); setHidden(readHidden()) }
    refresh()
    window.addEventListener(EVENT, refresh)
    const unsubscribe = subscribeDocuments(refresh)
    return () => { window.removeEventListener(EVENT, refresh); unsubscribe() }
  }, [])
  return hidden
}

/** A gateway node's details with the reader's hidden channels taken out:
 *  each hidden cell removed, then any row, group or family left empty. */
export function withoutHidden<T extends { families: { groups: { rows: { cells: { parameter_code: string | null }[] }[] }[] }[] }>(
  detail: T, station: string, hidden: ReadonlySet<string>): T {
  if (!hidden.size) return detail
  const families = detail.families.map(family => ({ ...family, groups: family.groups.map(group => ({ ...group,
    rows: group.rows.map(row => ({ ...row, cells: row.cells.filter(cell => !cell.parameter_code || !hidden.has(`${station}|${cell.parameter_code}`)) }))
      .filter(row => row.cells.length) })).filter(group => group.rows.length) })).filter(family => family.groups.length)
  return { ...detail, families }
}

/** How many of a station's channels are hidden. */
export function hiddenAt(hidden: ReadonlySet<string>, station: string): number {
  return [...hidden].filter((id) => id.startsWith(`${station}|`)).length
}
